import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Truck, Package, TrendingUp, TrendingDown, DollarSign, BarChart3,
  CheckCircle2, MapPin, Activity, Webhook, Copy, Check, ExternalLink, Loader2,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';
import { EnviaBalanceWidget } from '../../components/EnviaBalanceWidget';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: '7d',  label: '7 días' },
  { value: '30d', label: '30 días' },
  { value: '90d', label: '90 días' },
];

const PIE_PALETTE = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#ef4444', '#84cc16'];

const CHART = {
  delivered: '#22c55e',
  failed:    '#ef4444',
  in_transit:'#3b82f6',
  grid:      'rgba(255,255,255,0.06)',
  axis:      '#94a3b8',
};

const TOOLTIP_STYLE = {
  backgroundColor: '#1e293b',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: '#f1f5f9',
  fontSize: 12,
  padding: '8px 12px',
};

function formatMXN(n) {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
}

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

function deliveryRateBadge(rate) {
  if (rate >= 90) return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  if (rate >= 75) return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  return 'bg-rose-500/15 text-rose-300 border-rose-500/30';
}

export default function ShippingAnalytics() {
  const { token } = useAuth();
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/envia/admin/shipping-analytics?period=${period}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error('No pudimos cargar la analítica de envíos');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => { cancelled = true; };
  }, [period, token]);

  const summary = data?.summary;
  const byCarrier = data?.by_carrier || [];
  const byStatusOverTime = data?.by_status_over_time || [];
  const topDestinations = data?.top_destinations || [];

  const lineChartData = useMemo(
    () => byStatusOverTime.map(d => ({ ...d, label: formatShortDate(d.date) })),
    [byStatusOverTime]
  );

  const barChartData = useMemo(
    () => byCarrier.map(c => ({ carrier: c.carrier, tasa: c.delivery_rate })),
    [byCarrier]
  );

  const pieChartData = useMemo(
    () => byCarrier.map(c => ({ name: c.carrier, value: c.total })),
    [byCarrier]
  );

  const totalShipments = summary?.total_shipments ?? 0;
  const trendPct = summary?.trend_pct ?? 0;
  const trendUp = trendPct >= 0;

  return (
    <div
      className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-[calc(100vh-3.5rem)] bg-gradient-to-b from-[#0b1220] via-[#0a1020] to-[#0d1424] text-slate-100"
      data-testid="shipping-analytics-wrapper"
    >
      <div className="space-y-8 max-w-[1400px]" data-testid="shipping-analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="border-l-2 border-blue-500 pl-3">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-100 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-blue-400" />
            Analítica de Envíos
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Métricas operativas de logística agregadas por período.
          </p>
        </div>

        {/* Pill tabs */}
        <div className="inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm" data-testid="period-tabs">          {PERIOD_OPTIONS.map(opt => {
            const active = period === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                data-testid={`period-tab-${opt.value}`}
                className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors duration-150 ${
                  active
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-400 hover:text-slate-100'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary Cards (gradients) */}
      <section className="space-y-4">
        {/* Envia balance monitor — top right of summary */}
        <div className="flex justify-end" data-testid="balance-widget-slot">
          <div className="w-full max-w-md">
            <EnviaBalanceWidget variant="compact" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="border-white/8 bg-white/5">
                <CardContent className="p-5">
                  <Skeleton className="h-3 w-24 mb-3 bg-white/10" />
                  <Skeleton className="h-8 w-32 mb-2 bg-white/10" />
                  <Skeleton className="h-3 w-20 bg-white/10" />
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              {/* Total shipments — BLUE */}
              <GradientCard
                testid="card-total-shipments"
                gradient="from-blue-950/40 to-blue-900/20"
                border="border-blue-800/30"
                glow="shadow-blue-500/5"
                accent="text-blue-400"
                icon={Package}
                label="Total envíos"
              >
                <p className="text-3xl font-semibold text-slate-100 tabular-nums" data-testid="total-shipments-value">
                  {totalShipments.toLocaleString('es-MX')}
                </p>
                <div className="flex items-center gap-1.5 mt-2">
                  {trendUp ? (
                    <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5 text-rose-400" />
                  )}
                  <span className={`text-xs font-medium ${trendUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {trendUp ? '+' : ''}{trendPct.toFixed(1)}%
                  </span>
                  <span className="text-xs text-slate-400">vs período anterior</span>
                </div>
              </GradientCard>

              {/* Delivery rate — GREEN */}
              <GradientCard
                testid="card-delivery-rate"
                gradient="from-emerald-950/40 to-emerald-900/20"
                border="border-emerald-800/30"
                glow="shadow-emerald-500/5"
                accent="text-emerald-400"
                icon={CheckCircle2}
                label="Tasa de entrega"
              >
                <p
                  className="text-3xl font-semibold tabular-nums text-emerald-300"
                  data-testid="delivery-rate-value"
                >
                  {summary.delivery_rate.toFixed(1)}%
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  {summary.delivered} entregados de {summary.total_shipments}
                </p>
              </GradientCard>

              {/* Total cost — AMBER */}
              <GradientCard
                testid="card-total-cost"
                gradient="from-amber-950/40 to-amber-900/20"
                border="border-amber-800/30"
                glow="shadow-amber-500/5"
                accent="text-amber-400"
                icon={DollarSign}
                label="Costo total"
              >
                <p className="text-3xl font-semibold text-slate-100 tabular-nums" data-testid="total-cost-value">
                  {formatMXN(summary.total_shipping_cost_mxn)}
                </p>
                <p className="text-xs text-slate-400 mt-2">Acumulado en MXN</p>
              </GradientCard>

              {/* Avg cost — PURPLE */}
              <GradientCard
                testid="card-avg-cost"
                gradient="from-purple-950/40 to-purple-900/20"
                border="border-purple-800/30"
                glow="shadow-purple-500/5"
                accent="text-purple-400"
                icon={Activity}
                label="Costo promedio"
              >
                <p className="text-3xl font-semibold text-slate-100 tabular-nums" data-testid="avg-cost-value">
                  {formatMXN(summary.avg_shipping_cost_mxn)}
                </p>
                <p className="text-xs text-slate-400 mt-2">Por envío</p>
              </GradientCard>
            </>
          )}
        </div>

        {/* Status Mini Stats */}
        {!loading && summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="status-mini-stats">
            <MiniStat label="Entregados" value={summary.delivered} color="text-emerald-300" bg="bg-emerald-500/10 border-emerald-500/20" testid="mini-delivered" />
            <MiniStat label="En tránsito" value={summary.in_transit} color="text-blue-300" bg="bg-blue-500/10 border-blue-500/20" testid="mini-in-transit" />
            <MiniStat label="Fallidos"   value={summary.failed}     color="text-rose-300" bg="bg-rose-500/10 border-rose-500/20"     testid="mini-failed" />
            <MiniStat label="Pendientes" value={summary.pending}    color="text-slate-100" bg="bg-white/5 border-white/10"           testid="mini-pending" />
          </div>
        )}
      </section>

      <Divider />

      {/* Line Chart - Shipments over time */}
      <section className="space-y-4" data-testid="line-chart-section">
        <SectionTitle title="Evolución por estado" subtitle="Envíos diarios en el período seleccionado" />

        <Card className="border border-white/8 bg-transparent shadow-lg" data-testid="line-chart-card">
          <CardContent className="p-5 bg-transparent">
            {loading ? (
              <Skeleton className="h-72 w-full bg-white/5" />
            ) : lineChartData.length === 0 ? (
              <EmptyState message="Sin datos para este período" />
            ) : (
              <div className="h-72 w-full bg-transparent" data-testid="line-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineChartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke={CHART.axis}
                      tick={{ fill: CHART.axis, fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: CHART.grid }}
                    />
                    <YAxis
                      stroke={CHART.axis}
                      tick={{ fill: CHART.axis, fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: CHART.grid }}
                      allowDecimals={false}
                    />
                    <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: 'rgba(255,255,255,0.1)' }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: CHART.axis }} iconType="line" />
                    <Line type="monotone" dataKey="delivered"  name="Entregados"  stroke={CHART.delivered}  strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="failed"     name="Fallidos"    stroke={CHART.failed}     strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="in_transit" name="En tránsito" stroke={CHART.in_transit} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <Divider />

      {/* Two charts side by side */}
      <section className="space-y-4">
        <SectionTitle title="Desempeño por paquetería" subtitle="Comparativa de tasa de entrega y volumen" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Bar chart - delivery rate by carrier */}
          <Card className="border border-white/8 bg-transparent shadow-lg" data-testid="bar-chart-card">
            <CardContent className="p-5 bg-transparent">
              <h3 className="text-sm font-medium text-slate-100 mb-1">Tasa de entrega por paquetería</h3>
              <p className="text-xs text-slate-400 mb-4">Porcentaje de envíos entregados con éxito</p>
              {loading ? (
                <Skeleton className="h-64 w-full bg-white/5" />
              ) : barChartData.length === 0 ? (
                <EmptyState message="Aún no hay envíos registrados" />
              ) : (
                <div className="h-64 w-full bg-transparent" data-testid="bar-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"   stopColor="#3b82f6" stopOpacity={0.95} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.45} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                      <XAxis
                        dataKey="carrier"
                        stroke={CHART.axis}
                        tick={{ fill: CHART.axis, fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: CHART.grid }}
                      />
                      <YAxis
                        stroke={CHART.axis}
                        tick={{ fill: CHART.axis, fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: CHART.grid }}
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        contentStyle={TOOLTIP_STYLE}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        formatter={(v) => [`${v}%`, 'Tasa entrega']}
                      />
                      <Bar dataKey="tasa" name="Tasa entrega" radius={[6, 6, 0, 0]} fill="url(#barGradient)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pie chart - shipment distribution */}
          <Card className="border border-white/8 bg-transparent shadow-lg" data-testid="pie-chart-card">
            <CardContent className="p-5 bg-transparent">
              <h3 className="text-sm font-medium text-slate-100 mb-1">Distribución por paquetería</h3>
              <p className="text-xs text-slate-400 mb-4">Volumen total de envíos por carrier</p>
              {loading ? (
                <Skeleton className="h-64 w-full bg-white/5" />
              ) : pieChartData.length === 0 ? (
                <EmptyState message="Aún no hay envíos registrados" />
              ) : (
                <div className="h-64 w-full bg-transparent" data-testid="pie-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={55}
                        paddingAngle={2}
                        stroke="transparent"
                      >
                        {pieChartData.map((_, idx) => (
                          <Cell key={idx} fill={PIE_PALETTE[idx % PIE_PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Legend
                        wrapperStyle={{ fontSize: 12, color: CHART.axis }}
                        iconType="circle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <Divider />

      {/* Carrier Performance Table */}
      <section className="space-y-4">
        <SectionTitle
          title="Detalle por paquetería"
          subtitle="Ordenado por volumen total · Tasa de entrega coloreada"
          icon={Truck}
        />
        <Card className="border border-white/8 bg-transparent shadow-lg" data-testid="carrier-table-card">
          <CardContent className="p-5 bg-transparent">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full bg-white/5" />
                ))}
              </div>
            ) : byCarrier.length === 0 ? (
              <EmptyState message="Sin datos de carriers en este período" />
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="carrier-table">
                  <TableHeader>
                    <TableRow className="border-white/8 hover:bg-transparent">
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium">Paquetería</TableHead>
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium text-right">Total envíos</TableHead>
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium text-right">Entregados</TableHead>
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium text-right">Fallidos</TableHead>
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium text-right">Tasa entrega</TableHead>
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium text-right">Costo prom.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byCarrier.map((c, idx) => (
                      <TableRow
                        key={c.carrier}
                        className={`border-white/8 transition-colors hover:bg-white/5 ${idx % 2 === 0 ? 'bg-secondary/20' : 'bg-transparent'}`}
                        data-testid={`carrier-row-${c.carrier}`}
                      >
                        <TableCell className="font-medium text-slate-100 capitalize">{c.carrier}</TableCell>
                        <TableCell className="text-right text-slate-100 tabular-nums">{c.total}</TableCell>
                        <TableCell className="text-right text-emerald-300 tabular-nums">{c.delivered}</TableCell>
                        <TableCell className="text-right text-rose-300 tabular-nums">{c.failed}</TableCell>
                        <TableCell className="text-right">
                          <span
                            className={`inline-flex items-center justify-center min-w-[64px] px-2.5 py-1 rounded-full border tabular-nums text-xs font-semibold ${deliveryRateBadge(c.delivery_rate)}`}
                            data-testid={`delivery-rate-badge-${c.carrier}`}
                          >
                            {c.delivery_rate.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-slate-100 tabular-nums">
                          {formatMXN(c.avg_cost_mxn)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <Divider />

      {/* Top destinations */}
      <section className="space-y-4">
        <SectionTitle
          title="Top 5 destinos"
          subtitle="Ciudades con mayor volumen de envíos"
          icon={MapPin}
        />
        <Card className="border border-white/8 bg-transparent shadow-lg" data-testid="destinations-card">
          <CardContent className="p-5 bg-transparent">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full bg-white/5" />
                ))}
              </div>
            ) : topDestinations.length === 0 ? (
              <EmptyState message="Sin destinos para este período" />
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="destinations-table">
                  <TableHeader>
                    <TableRow className="border-white/8 hover:bg-transparent">
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium">Ciudad</TableHead>
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium">Estado</TableHead>
                      <TableHead className="text-slate-400 text-[11px] uppercase tracking-wider font-medium text-right">Envíos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topDestinations.map((d, i) => (
                      <TableRow
                        key={`${d.city}-${d.state}-${i}`}
                        className={`border-white/8 transition-colors hover:bg-white/5 ${i % 2 === 0 ? 'bg-secondary/20' : 'bg-transparent'}`}
                        data-testid={`destination-row-${i}`}
                      >
                        <TableCell className="font-medium text-slate-100">{d.city || '—'}</TableCell>
                        <TableCell className="text-slate-400">{d.state || '—'}</TableCell>
                        <TableCell className="text-right text-slate-100 tabular-nums">{d.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <Divider />

      {/* Webhook configuration */}
      <WebhookConfigSection />
      </div>
    </div>
  );
}

/* ---------- Sub components ---------- */

function GradientCard({ testid, gradient, border, glow, accent, icon: Icon, label, children }) {
  return (
    <Card
      data-testid={testid}
      className={`relative overflow-hidden border ${border} bg-gradient-to-br ${gradient} shadow-lg ${glow}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            {label}
          </p>
          <Icon className={`h-4 w-4 ${accent}`} />
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, color, bg, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg border px-4 py-3 ${bg}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</p>
      <p className={`text-xl font-semibold tabular-nums mt-1 ${color}`}>
        {Number(value || 0).toLocaleString('es-MX')}
      </p>
    </div>
  );
}

function SectionTitle({ title, subtitle, icon: Icon }) {
  return (
    <div className="border-l-2 border-blue-500 pl-3">
      <h2 className="text-base font-semibold text-slate-100 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        {title}
      </h2>
      {subtitle && (
        <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
      )}
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-white/5" aria-hidden="true" />;
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state">
      <Package className="h-8 w-8 text-slate-400/60 mb-2" />
      <p className="text-sm text-slate-400">{message}</p>
    </div>
  );
}


function WebhookConfigSection() {
  const { token } = useAuth();
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/envia/webhook`;
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRegistered, setSavingRegistered] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/envia/admin/webhook-config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setConfig(r.data);
      } catch (err) {
        // graceful: si falla, asumimos no configurado
        if (!cancelled) setConfig({ webhook_url: null, is_registered: false, events_received_30d: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => { cancelled = true; };
  }, [token]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      toast.success('URL copiada al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar al portapapeles');
    }
  };

  const handleMarkRegistered = async (registered) => {
    setSavingRegistered(true);
    try {
      const r = await axios.post(
        `${API}/envia/admin/webhook-config`,
        { webhook_url: webhookUrl, is_registered: registered },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig((prev) => ({
        ...(prev || {}),
        webhook_url: r.data.webhook_url,
        is_registered: r.data.is_registered,
        updated_at: r.data.updated_at,
      }));
      toast.success(registered
        ? 'Webhook marcado como configurado'
        : 'Webhook marcado como pendiente'
      );
    } catch {
      toast.error('No se pudo actualizar el estado del webhook');
    } finally {
      setSavingRegistered(false);
    }
  };

  const isRegistered = !!config?.is_registered;
  const eventsCount = config?.events_received_30d || 0;
  const lastEventAt = config?.last_event_at;

  return (
    <section className="space-y-4" data-testid="webhook-config-section">
      <div className="border-l-2 border-blue-500 pl-3">
        <h2 className="text-base font-semibold text-slate-100 flex items-center gap-2">
          <Webhook className="h-4 w-4 text-slate-400" />
          Configuración de Webhooks
        </h2>
        <p className="text-xs text-slate-400 mt-0.5">
          Recibe actualizaciones de estado de envío en tiempo real desde Envia.com.
        </p>
      </div>

      <Card className="border border-white/8 bg-transparent shadow-lg" data-testid="webhook-config-card">
        <CardContent className="p-5 space-y-5">
          {/* Status indicator */}
          {loading ? (
            <Skeleton className="h-6 w-48 bg-white/5" />
          ) : (
            <div
              data-testid="webhook-status-indicator"
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${
                isRegistered
                  ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                  : 'bg-amber-500/15 border-amber-500/30 text-amber-300'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${isRegistered ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
              {isRegistered ? 'Webhook configurado' : 'Webhook pendiente de configuración'}
              {isRegistered && eventsCount > 0 && (
                <span className="text-emerald-200/80">·&nbsp;{eventsCount} eventos en 30 días</span>
              )}
            </div>
          )}

          {/* Webhook URL + Copy */}
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
              URL del Webhook
            </label>
            <div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2">
              <code
                className="flex-1 text-xs sm:text-sm text-slate-200 font-mono break-all select-all"
                data-testid="webhook-url"
              >
                {webhookUrl}
              </code>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleCopy}
                data-testid="webhook-copy-btn"
                className="shrink-0 text-slate-300 hover:text-slate-100 hover:bg-white/10 gap-1.5"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          </div>

          {/* Step by step instructions */}
          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
              Pasos para registrar en Envia.com
            </p>
            <ol className="space-y-2.5" data-testid="webhook-instructions">
              {[
                'Copia la URL de webhook',
                <>Entra a tu panel de Envia.com → <span className="text-slate-200">Configuración</span> → <span className="text-slate-200">Webhooks</span></>,
                <>Pega la URL y selecciona los eventos: <code className="text-blue-300 bg-blue-500/10 px-1.5 py-0.5 rounded text-[11px]">shipment.delivered</code>, <code className="text-blue-300 bg-blue-500/10 px-1.5 py-0.5 rounded text-[11px]">shipment.in_transit</code>, <code className="text-blue-300 bg-blue-500/10 px-1.5 py-0.5 rounded text-[11px]">shipment.failed</code></>,
                'Guarda los cambios',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`webhook-step-${i + 1}`}>
                  <span className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[11px] font-semibold flex items-center justify-center tabular-nums">
                    {i + 1}
                  </span>
                  <span className="text-sm text-slate-300 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>

            <a
              href="https://envia.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors duration-150"
              data-testid="envia-dashboard-link"
            >
              <ExternalLink className="h-3 w-3" />
              Abrir panel de Envia.com
            </a>
          </div>

          {/* Mark as registered action */}
          <div className="pt-4 border-t border-white/8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-slate-400 max-w-md leading-relaxed">
              Una vez configurado, los estados de envío se actualizarán automáticamente sin necesidad de sincronización manual.
            </p>
            <Button
              type="button"
              size="sm"
              variant={isRegistered ? 'outline' : 'default'}
              disabled={loading || savingRegistered}
              onClick={() => handleMarkRegistered(!isRegistered)}
              data-testid="webhook-toggle-registered-btn"
              className="shrink-0 gap-1.5"
            >
              {savingRegistered ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : isRegistered ? (
                <Check className="h-3.5 w-3.5" />
              ) : null}
              {isRegistered ? 'Marcado como configurado' : 'Marcar como configurado'}
            </Button>
          </div>

          {lastEventAt && (
            <p className="text-[11px] text-slate-500 tabular-nums" data-testid="webhook-last-event">
              Último evento recibido: {new Date(lastEventAt).toLocaleString('es-MX')}
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
